/**
 * streamReveal — paced reveal for streamed answer text (owner directive
 * 2026-08-23: "enable smooth streaming in from any of the models … this is a
 * new consistency in the Prest0 platform").
 *
 * WHY THIS EXISTS
 * The platform has four answer-delivery lanes and none of them read as smooth
 * on their own:
 *   • the async/202 lane (today's fleet default, so ~all real traffic) polls
 *     every 2s and hands the client the ENTIRE answer in one call — a pop-in;
 *   • the dedicated-VM lane emits real provider deltas, but bursty and
 *     unevenly (and until the same-day wire-shape fix they were dropped);
 *   • the CF SSE lane emits one `text` event carrying the whole answer;
 *   • partial-answer relay (taskQueue) lands a growing prefix every few
 *     seconds — big steps, not a flow.
 * Painting whatever arrives, whenever it arrives, makes each lane FEEL like a
 * different product. This module is the one place that decides how fast text
 * appears, so every lane looks the same: text flows in.
 *
 * CONTRACT
 * The caller keeps owning the real accumulated text — the reveal only decides
 * how much of it is PAINTED right now. Finalize always writes the full text
 * over whatever was revealed, so a reveal can never lose content; `flush()`
 * (or `cancel()`) at every turn-terminal path stops a queued frame from
 * repainting stale text on top of a finalized message.
 *
 * PACING
 * A base rate keeps well ahead of real provider throughput (~120-320 chars/s),
 * so a genuine token stream is never held back — the pacing only smooths the
 * bursts. A large backlog (the pop-in lanes) drains on a bounded deadline
 * instead, so a long answer reveals quickly rather than crawling. Both are
 * derived per frame from the real elapsed time, so a slow/throttled frame
 * clock catches up on the next frame instead of falling behind.
 *
 * Pure and injectable (clock + frame scheduler) so the pacing unit-tests
 * without a browser.
 */

/** Steady-state reveal speed, characters per second. */
export const REVEAL_BASE_CHARS_PER_SEC = 420;

/** A backlog must be fully revealed within this long, however big it is. */
export const REVEAL_MAX_DRAIN_MS = 1_200;

/** Never advance by less than this per frame once there is anything to show. */
export const REVEAL_MIN_CHARS_PER_FRAME = 1;

export interface StreamRevealHandle {
  /** Set the full text revealed SO FAR by the transport (monotonic prefix). */
  push: (fullText: string) => void;
  /** Paint everything immediately and stop. Safe to call repeatedly. */
  flush: () => void;
  /** Stop without painting anything further (thread switch, abort). */
  cancel: () => void;
  /** Characters currently painted — for tests and diagnostics. */
  revealedLength: () => number;
}

export interface StreamRevealOptions {
  /** Paint callback — receives the visible PREFIX of the pushed text. */
  onPaint: (visibleText: string) => void;
  /**
   * Reveal instantly, no pacing. Set for `prefers-reduced-motion`, and used
   * automatically when no frame scheduler is available (SSR, jsdom without
   * rAF): correctness never depends on the animation running.
   */
  immediate?: boolean;
  /** Injectable clock (ms). Defaults to Date.now. */
  now?: () => number;
  /** Injectable frame scheduler. Defaults to requestAnimationFrame. */
  scheduleFrame?: (cb: () => void) => number;
  /** Injectable frame canceller. Defaults to cancelAnimationFrame. */
  cancelFrame?: (handle: number) => void;
}

/** Length of the shared leading run of two strings. */
export function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a.charCodeAt(i) === b.charCodeAt(i)) i += 1;
  return i;
}

/**
 * How many characters to advance this frame, given the backlog and how long
 * the frame actually took. Pure — the pacing rule, extracted for tests.
 */
export function revealCharsForFrame(params: {
  backlog: number;
  elapsedMs: number;
}): number {
  const backlog = Math.max(0, Math.floor(params.backlog));
  if (backlog === 0) return 0;
  // Clamp the frame delta: a backgrounded tab can hand back a multi-second
  // elapsed, which would otherwise dump the whole backlog in one paint.
  const elapsed = Math.min(Math.max(params.elapsedMs, 0), 100);
  const steady = (REVEAL_BASE_CHARS_PER_SEC * elapsed) / 1000;
  // Deadline pressure: whatever is queued must be gone within the drain
  // window, so a 6,000-character pop-in reveals in ~1.2s rather than ~14s.
  const deadline = (backlog * elapsed) / REVEAL_MAX_DRAIN_MS;
  const advance = Math.ceil(Math.max(steady, deadline, REVEAL_MIN_CHARS_PER_FRAME));
  return Math.min(advance, backlog);
}

/**
 * Create a reveal buffer. Painting starts on the first `push` with content and
 * stops on its own once the revealed text has caught up with the target — no
 * idle frame loop between chunks.
 */
export function createStreamReveal(options: StreamRevealOptions): StreamRevealHandle {
  const now = options.now
    || (() => Date.now());
  const scheduleFrame = options.scheduleFrame
    || (typeof requestAnimationFrame === 'function'
      ? (cb: () => void) => requestAnimationFrame(cb)
      : null);
  const cancelFrame = options.cancelFrame
    || (typeof cancelAnimationFrame === 'function'
      ? (handle: number) => cancelAnimationFrame(handle)
      : null);
  // No scheduler (SSR, or a jsdom without rAF) ⇒ behave exactly like today:
  // paint whatever arrives, immediately. Smoothing is an enhancement, and the
  // text must render either way.
  const immediate = options.immediate === true || !scheduleFrame || !cancelFrame;

  let target = '';
  let revealed = 0;
  let frame: number | null = null;
  let lastFrameAt = 0;
  let stopped = false;

  const paint = (): void => {
    try {
      options.onPaint(target.slice(0, revealed));
    } catch {
      // A paint failure must never kill the stream — the transport keeps
      // accumulating and finalize still writes the whole answer.
    }
  };

  const stopFrame = (): void => {
    if (frame !== null && cancelFrame) {
      cancelFrame(frame);
    }
    frame = null;
  };

  const step = (): void => {
    frame = null;
    if (stopped) return;
    const at = now();
    const elapsed = lastFrameAt ? at - lastFrameAt : 16;
    lastFrameAt = at;
    revealed = Math.min(
      target.length,
      revealed + revealCharsForFrame({ backlog: target.length - revealed, elapsedMs: elapsed }),
    );
    paint();
    if (revealed < target.length) armFrame();
  };

  function armFrame(): void {
    if (stopped || frame !== null || !scheduleFrame) return;
    frame = scheduleFrame(step);
  }

  return {
    push(fullText: string) {
      if (stopped) return;
      const next = typeof fullText === 'string' ? fullText : '';
      // The transport is append-only, but a lane can REPLACE the text: a
      // sanitized rewrite, or the file-dump mask swapping a labeled dump for
      // a shorter "Generating <name>…" line. Rewind only to where the two
      // texts actually diverge, never to zero — resetting the whole reveal
      // would blank the bubble and re-type it from scratch.
      if (!next.startsWith(target.slice(0, revealed))) {
        revealed = commonPrefixLength(target.slice(0, revealed), next);
      }
      target = next;
      if (revealed >= target.length) return;
      if (immediate) {
        revealed = target.length;
        paint();
        return;
      }
      if (!lastFrameAt) lastFrameAt = now();
      armFrame();
    },
    flush() {
      stopped = true;
      stopFrame();
      if (revealed < target.length) {
        revealed = target.length;
        paint();
      }
    },
    cancel() {
      stopped = true;
      stopFrame();
    },
    revealedLength: () => revealed,
  };
}

/** Does this viewer want motion minimized? Safe on any environment. */
export function prefersReducedMotion(): boolean {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

export default createStreamReveal;
